import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import TopBar from '../../Tools/Topbar';
import LeftSide from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import Pagination from '../../Tools/Pagination';
import AddCaseModal from './EMSCases/AddCaseModal';
import EditCaseModal from './EMSCases/EditCaseModal';
import LocationDropdown from '../../tabs/Dropdown/LocationName';
import TransferCaseModal from '../TransferCasesTab/TransferCaseModal/TranferCaseaddmodal';
import { format, parseISO } from 'date-fns';

// Component to fetch and store date and time formats in sessionStorage
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: {
            Authorization: 'Bearer 1349|VFSxfHmNSspz9ZhH1Nu9L5yxoD9vlzPoI9BKifiE324f1ef0',
          },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date and time formats:', err);
        // Fallback to defaults if API fails
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []); // Run once on mount

  return null; // No UI to render
};

// Main component for managing EMS cases, transfer cases, and hold cases
const CasesTab = () => {
  // State for controlling sidebar visibility
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // State for controlling add/edit modal visibility
  const [showModal, setShowModal] = useState(false);
  // State to toggle between add and edit modes
  const [isEditMode, setIsEditMode] = useState(false);
  // State to store the ID of the case being edited
  const [currentCaseId, setCurrentCaseId] = useState(null);
  // State to indicate loading status
  const [isLoading, setIsLoading] = useState(false);
  // State to store EMS cases, transfer cases, and hold cases
  const [cases, setCases] = useState([]);
  const [transferCases, setTransferCases] = useState([]);
  const [holdCases, setHoldCases] = useState([]);
  // State to store filtered versions of cases
  const [filteredCases, setFilteredCases] = useState([]);
  const [filteredTransferCases, setFilteredTransferCases] = useState([]);
  const [filteredHoldCases, setFilteredHoldCases] = useState([]);
  // State for pagination
  const [page, setPage] = useState(1);
  const [transferPage, setTransferPage] = useState(1);
  const [holdPage, setHoldPage] = useState(1);
  // State for transfer case modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferModalMode, setTransferModalMode] = useState('add');
  const [selectedTransferCase, setSelectedTransferCase] = useState(null);
  // State for user role ID
  const [roleId, setRoleId] = useState(null);
  // State for filter inputs
  const [filterForm, setFilterForm] = useState({ case_name: '', location_id: '' });
  const [transferFilter, setTransferFilter] = useState({ name: '' });
  const [holdFilter, setHoldFilter] = useState({ case_name: '', location_id: '' });
  // State for selected case in map dropdown
  const [selectedCase, setSelectedCase] = useState('All');
  // State for form data used in add/edit modals
  const [formData, setFormData] = useState({
    user_id: sessionStorage.getItem('user_id'),
    case_name: '',
    case_location: '',
    case_location_name: '',
    case_area: '',
    nearest_medical_center: '',
    injury_type: '',
    age: '',
    gender: '',
    triage_card: '',
    bracelet_id: '',
    contact_number: '',
    documents: [],
    ems_member: '',
    ems_member_name: '',
    nearest_ambulance: '',
    patient_location: '',
    patient_area: '',
    accident_place: '',
  });

  // Hooks for navigation and settings
  const location = useLocation();
  const navigate = useNavigate();
  const { settings, settingsRef, updateSetting, handleColorTheme } = useSettings();
  // Constant for items per page in pagination
  const limit = 10;

  // Get date and time format from sessionStorage
  const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
  const timeFormat = sessionStorage.getItem('time_format') || '24';

  // Utility function to format date based on settings
  const formatCaseDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
      const timeFormat = sessionStorage.getItem('time_format') || '24';

      // Map WordPress-style formats to date-fns syntax
      const dateFormatMap = {
        'DD/MM/YYYY': 'dd/MM/yyyy',
        'MM/DD/YYYY': 'MM/dd/yyyy',
        'YYYY-MM-DD': 'yyyy-MM-dd',
      };

      const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
      const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;

      //  Parse both ISO and non-ISO timestamps as *UTC*, not local time
      let utcDate;
      if (dateString.includes('T')) {
        // ISO 8601 with timezone (e.g. 2025-10-27T16:27:19.000000Z)
        utcDate = new Date(dateString);
      } else {
        // MySQL-like (e.g. 2025-10-27 16:35:40)
        utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // force UTC
      }

      // Format without applying local timezone
      const utcTimestamp = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate(),
        utcDate.getUTCHours(),
        utcDate.getUTCMinutes(),
        utcDate.getUTCSeconds()
      );

      return format(utcTimestamp, formatString);
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return 'Invalid Date';
    }
  };

  // Effect to initialize roleId and check for modal opening from URL
  useEffect(() => {
    const storedRoleId = sessionStorage.getItem('Role_id');
    if (storedRoleId) setRoleId(storedRoleId);
    if (location.search.includes('openModal=true')) {
      setShowModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  // Effect to fetch all cases when roleId is available
  useEffect(() => {
    if (roleId) fetchAllCases();
  }, [roleId]);

  // Effect to filter cases based on filter inputs
  useEffect(() => {
    setFilteredCases(
      cases.filter(
        (caseItem) =>
          (!filterForm.case_name ||
            caseItem.case_name.toLowerCase().includes(filterForm.case_name.toLowerCase())) &&
          (!filterForm.location_id || caseItem.case_location === parseInt(filterForm.location_id))
      )
    );
    setFilteredTransferCases(
      transferCases.filter(
        (caseItem) =>
          !transferFilter.name || caseItem.case_name.toLowerCase().includes(transferFilter.name.toLowerCase())
      )
    );
    setFilteredHoldCases(
      holdCases.filter(
        (caseItem) =>
          (!holdFilter.case_name ||
            caseItem.case_name.toLowerCase().includes(holdFilter.case_name.toLowerCase())) &&
          (!holdFilter.location_id || caseItem.case_location === parseInt(holdFilter.location_id))
      )
    );
  }, [cases, transferCases, holdCases, filterForm, transferFilter, holdFilter]);

  // Function to fetch all cases (EMS, transfer, and hold) from the API
  const fetchAllCases = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/ems/records/${roleId}/ems_cases/${sessionStorage.getItem('user_id')}`);
      setCases(response.data?.newcase || []);
      setTransferCases(response.data?.transfercase || []);
      setHoldCases(response.data?.holdcase || []);
    } catch (error) {
      console.error('Error fetching cases:', error);
      alert('Error: Failed to fetch cases');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle input changes in the add/edit form
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Fetch employee and vehicle data when case_area changes
    if (name === 'case_area' && value) {
      try {
        const res = await axiosInstance.get(`/ems/resource-avilability-check/${value}`);
        const { employee, vehicle } = res.data.data;
        setFormData((prev) => ({
          ...prev,
          ems_member: employee.employee_id,
          ems_member_name: employee.name,
          nearest_ambulance: vehicle.vehicle_id,
        }));
      } catch (err) {
        console.error('Error fetching employee/vehicle:', err);
      }
    }
  };

  // Function to handle changes in EMS case filter inputs
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  // Function to handle changes in transfer case filter inputs
  const handleTransferFilterChange = (e) => {
    const { name, value } = e.target;
    setTransferFilter((prev) => ({ ...prev, [name]: value }));
  };

  // Function to handle changes in hold case filter inputs
  const handleHoldFilterChange = (e) => {
    const { name, value } = e.target;
    setHoldFilter((prev) => ({ ...prev, [name]: value }));
  };

  // Functions to clear filter inputs
  const clearFilters = () => setFilterForm({ case_name: '', location_id: '' });
  const clearTransferFilters = () => setTransferFilter({ name: '' });
  const clearHoldFilters = () => setHoldFilter({ case_name: '', location_id: '' });

  // Function to open the add case modal
  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentCaseId(null);
    setFormData({
      user_id: sessionStorage.getItem('user_id'),
      case_name: '',
      case_location: '',
      case_location_name: '',
      case_area: '',
      nearest_medical_center: '',
      injury_type: '',
      age: '',
      gender: '',
      triage_card: '',
      bracelet_id: '',
      contact_number: '',
      documents: [],
      ems_member: '',
      ems_member_name: '',
      nearest_ambulance: '',
      patient_location: '',
      patient_area: '',
      accident_place: '',
    });
    setShowModal(true);
  };

  // Function to handle editing a case
  const handleEdit = async (caseItem) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/ems/cases/${caseItem.id}`);
      const caseData = response.data.data || response.data;
      console.log('CasesTab: Fetched case data for ID', caseItem.id, ':', caseData);
      setIsEditMode(true);
      setCurrentCaseId(caseItem.id);
      const updatedFormData = {
        user_id: caseData.user_id || sessionStorage.getItem('user_id'),
        case_name: caseData.case_name || '',
        case_location: caseData.case_location || '',
        case_location_name: caseData.location?.location_name || caseData.case_location_name || '',
        case_area: caseData.case_area || '',
        nearest_medical_center: caseData.nearest_medical_center || '',
        injury_type: caseData.injury_type || '',
        age: caseData.age || '',
        gender: caseData.gender || '',
        triage_card: caseData.triage_card || '',
        bracelet_id: caseData.bracelet_id || '',
        contact_number: caseData.contact_number || '',
        documents: caseData.documents || [],
        ems_member: caseData.ems_member || '',
        ems_member_name: caseData.ems_member_name || '',
        nearest_ambulance: caseData.nearest_ambulance || '',
        patient_location: caseData.patient_location || caseData.p_location?.id || '',
        patient_area: caseData.patient_area || caseData.p_area?.id || '',
        accident_place: caseData.accident_place || '',
      };
      setFormData(updatedFormData);
      console.log('CasesTab: Set formData:', updatedFormData);
      setShowModal(true);
    } catch (error) {
      console.error('CasesTab: Error fetching case details:', error, 'Response:', error.response?.data);
      alert('Error: Failed to load case details');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to open the transfer case modal
  const openTransferModal = (mode, caseItem = null) => {
    setTransferModalMode(mode);
    setSelectedTransferCase(caseItem);
    setShowTransferModal(true);
  };

  // Function to navigate to EMS case details page
  const handleViewDetails = (caseId) => {
    sessionStorage.setItem('currentCaseId', caseId);
    navigate('/ems/case-detail');
  };

  // Function to navigate to transfer case details page
  const handleTransferDetails = (caseId) => {
    sessionStorage.setItem('currentCaseId', caseId);
    navigate('/ems/transfer-detail');
  };

  // Function to save a case (add or update)
  const handleSave = async (formDataToSend) => {
    try {
      setIsLoading(true);
      const url = isEditMode ? `/ems/cases/${currentCaseId}` : '/ems/cases';
      await axiosInstance.post(url, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${sessionStorage.getItem('auth_token')}`,
        },
      });
      alert(`Success: Case ${isEditMode ? 'updated' : 'added'} successfully!`);
      setShowModal(false);
      fetchAllCases();
    } catch (error) {
      console.error('CasesTab: Save failed:', error, 'Response:', error.response?.data);
      alert(`Error: ${error.response?.data?.message || 'Failed to save case'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to delete an EMS case
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this case?')) return;
    try {
      setIsLoading(true);
      await axiosInstance.delete(`/ems/cases/${id}`);
      alert('Success: Case deleted successfully!');
      fetchAllCases();
    } catch (error) {
      console.error('CasesTab: Delete failed:', error, 'Response:', error.response?.data);
      alert(`Error: ${error.response?.data?.message || 'Failed to delete case'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to delete a transfer case
  const handleDeleteTransferCase = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transfer case?')) return;
    try {
      setIsLoading(true);
      await axiosInstance.delete(`/ems/cases/${id}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem('auth_token')}` },
      });
      alert('Success: Transfer case deleted successfully!');
      fetchAllCases();
    } catch (error) {
      console.error('CasesTab: Delete failed:', error, 'Response:', error.response?.data);
      alert(`Error: ${error.response?.data?.message || 'Failed to delete transfer case'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to resubmit a hold case
  const handleResubmit = async (id) => {
    if (!window.confirm('Are you sure you want to resubmit this case?')) return;
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `/ems/resubmit-the-case/${id}`,
        {}, // empty body
        {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            Authorization: `Bearer 1059|qI6krv8rol5UGTuG2cWffcX57wSz6YlWEh5i68vXca519965`,
          },
        }
      );
      alert(`${response.data.message}`);
      fetchAllCases();
    } catch (error) {
      console.error('CasesTab: Resubmit failed:', error, 'Response:', error.response?.data);
      alert(`${error.response?.data?.message || 'Failed to resubmit case'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate paginated items for each case type
  const currentItems = filteredCases.slice((page - 1) * limit, page * limit);
  const currentTransferItems = filteredTransferCases.slice((transferPage - 1) * limit, transferPage * limit);
  const currentHoldItems = filteredHoldCases.slice((holdPage - 1) * limit, holdPage * limit);

  // Function to handle case selection change for map
  const handleCaseChange = (e) => setSelectedCase(e.target.value);

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />
      {/* Sidebar component */}
      <LeftSide isSidebarOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        {/* Top bar component */}
        <TopBar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <div className="body-wrapper">
          <div className="container-fluid">
            {/* Settings component */}
            <Settings ref={settingsRef} updateSetting={updateSetting} handleColorTheme={handleColorTheme} settings={settings} />

            {/* EMS Cases Section */}
            <div className="card w-100">
              <div className="card-header px-4 py-3">
                <h5 className="card-title fw-semibold mb-0">EMS Emergency Cases</h5>
                <p className="card-subtitle mt-1">View, manage, and track all emergency medical cases</p>
              </div>
              <div className="card-body p-4">
                {/* Filter section for EMS cases */}
                <div className="filter-search mt-4">
                  <h6 className="px-2 mb-2">Filter</h6>
                  <form className="row g-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        name="case_name"
                        placeholder="Case Name"
                        value={filterForm.case_name}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <LocationDropdown value={filterForm.location_id} onChange={handleFilterChange} name="location_id" />
                    </div>
                    <div className="col-md-4">
                      <button type="button" className="btn btn-outline-primary btn-rounded" onClick={clearFilters}>
                        Clear Filter
                      </button>
                    </div>
                  </form>
                  <button
                    className="btn btn-outline-primary mt-3"
                    onClick={openAddModal}
                    disabled={isLoading}
                  >
                    <i className="ti ti-circle-plus fs-4 me-2"></i>Add New Case
                  </button>
                </div>

                {/* EMS cases table */}
                <div className="table-responsive mt-4">
                  <table className="table text-nowrap align-middle">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Location</th>
                        <th>Medical Center</th>
                        <th>Case Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && !filteredCases.length ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status" />
                          </td>
                        </tr>
                      ) : !filteredCases.length ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">No Cases Found</td>
                        </tr>
                      ) : (
                        currentItems.map((caseItem) => (
                          <tr key={caseItem.id}>
                            <td>
                              <button className="text-primary border-0 bg-transparent" onClick={() => handleViewDetails(caseItem.id)}>
                                {caseItem.id}
                              </button>
                            </td>
                            <td>
                              <button className="text-primary border-0 bg-transparent" onClick={() => handleViewDetails(caseItem.id)}>
                                {caseItem.case_name}
                              </button>
                            </td>
                            <td>{caseItem.location?.location_name || caseItem.case_location_name || 'N/A'}</td>
                            <td>{caseItem.medical_center?.name || 'N/A'}</td>
                            <td>
                              {caseItem.created_at ? formatCaseDate(caseItem.created_at) : 'N/A'}
                            </td>
                            <td>
                              <button className="btn p-0 me-2 border-0" onClick={() => handleEdit(caseItem)} disabled={isLoading}>
                                <i className="ti ti-edit text-warning fs-5"></i>
                              </button>
                              <button className="btn p-0 me-2 border-0" onClick={() => handleDelete(caseItem.id)} disabled={isLoading}>
                                <i className="ti ti-trash text-danger fs-5"></i>
                              </button>
                              <button className="btn p-0 border-0" onClick={() => handleViewDetails(caseItem.id)} disabled={isLoading}>
                                <i className="ti ti-list-details text-success fs-5"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination for EMS cases */}
                {filteredCases.length > 0 && (
                  <Pagination total={filteredCases.length} limit={limit} page={page} onPageChange={setPage} disabled={isLoading} />
                )}
              </div>
            </div>

            {/* Transfer Cases Section */}
            <div className="card w-100">
              <div className="card-header px-4 py-3">
                <h5 className="card-title fw-semibold mb-0">Transfer Case Management</h5>
                <p className="card-subtitle mt-1">Manage patient transfer cases between facilities</p>
              </div>
              <div className="card-body p-4">
                {/* Filter section for transfer cases */}
                <div className="filter-search mt-4">
                  <h6 className="px-2 mb-2">Filter</h6>
                  <form className="row g-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        placeholder="Patient Name"
                        value={transferFilter.name}
                        onChange={handleTransferFilterChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <button type="button" className="btn btn-outline-primary btn-rounded" onClick={clearTransferFilters}>
                        Clear Filter
                      </button>
                    </div>
                  </form>
                  <button
                    className="btn btn-outline-primary mt-3"
                    onClick={() => openTransferModal('add')}
                    disabled={isLoading}
                  >
                    <i className="ti ti-circle-plus fs-4 me-2"></i>Add New Transfer Case
                  </button>
                </div>

                {/* Transfer cases table */}
                <div className="table-responsive mt-4">
                  <table className="table text-nowrap align-middle">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Transfer From</th>
                        <th>Transfer To</th>
                        <th>Case Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && !filteredTransferCases.length ? (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status" />
                          </td>
                        </tr>
                      ) : !filteredTransferCases.length ? (
                        <tr>
                          <td colSpan="10" className="text-center py-4">No Transfer Cases Found</td>
                        </tr>
                      ) : (
                        currentTransferItems.map((caseItem) => (
                          <tr key={caseItem.id}>
                            <td>
                              <button className="text-primary border-0 bg-transparent" onClick={() => handleTransferDetails(caseItem.id)}>
                                {caseItem.id}
                              </button>
                            </td>
                            <td>
                              <button className="text-primary border-0 bg-transparent" onClick={() => handleTransferDetails(caseItem.id)}>
                                {caseItem.case_name}
                              </button>
                            </td>
                            <td>{caseItem.from_medical_center?.name || caseItem.case_location_name || 'N/A'}</td>
                            <td>{caseItem.medical_center?.name || 'N/A'}</td>
                            <td>
                              {caseItem.created_at ? formatCaseDate(caseItem.created_at) : 'N/A'}
                            </td>
                            <td>
                              <button className="btn p-0 me-2 border-0" onClick={() => openTransferModal('edit', caseItem)} disabled={isLoading}>
                                <i className="ti ti-edit text-warning fs-5"></i>
                              </button>
                              <button className="btn p-0 me-2 border-0" onClick={() => handleDeleteTransferCase(caseItem.id)} disabled={isLoading}>
                                <i className="ti ti-trash text-danger fs-5"></i>
                              </button>
                              <button className="btn p-0 border-0" onClick={() => handleTransferDetails(caseItem.id)} disabled={isLoading}>
                                <i className="ti ti-list-details text-success fs-5"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination for transfer cases */}
                {filteredTransferCases.length > 0 && (
                  <Pagination total={filteredTransferCases.length} limit={limit} page={transferPage} onPageChange={setTransferPage} disabled={isLoading} />
                )}
              </div>
            </div>

            {/* Hold Cases Section */}
            <div className="card w-100">
              <div className="card-header px-4 py-3">
                <h5 className="card-title fw-semibold mb-0">Hold Case Management</h5>
                <p className="card-subtitle mt-1">View, manage, and track all hold cases</p>
              </div>
              <div className="card-body p-4">
                {/* Filter section for hold cases */}
                <div className="filter-search mt-4">
                  <h6 className="px-2 mb-2">Filter</h6>
                  <form className="row g-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        name="case_name"
                        placeholder="Case Name"
                        value={holdFilter.case_name}
                        onChange={handleHoldFilterChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <LocationDropdown value={holdFilter.location_id} onChange={handleHoldFilterChange} name="location_id" />
                    </div>
                    <div className="col-md-4">
                      <button type="button" className="btn btn-outline-primary btn-rounded" onClick={clearHoldFilters}>
                        Clear Filter
                      </button>
                    </div>
                  </form>
                </div>

                {/* Hold cases table */}
                <div className="table-responsive mt-4">
                  <table className="table text-nowrap align-middle">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Transfer From</th>
                        <th>Transfer To</th>
                        <th>Status</th>
                        <th>Case Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading && !filteredHoldCases.length ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status" />
                          </td>
                        </tr>
                      ) : !filteredHoldCases.length ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">No Hold Cases Found</td>
                        </tr>
                      ) : (
                        currentHoldItems.map((caseItem) => (
                          <tr key={caseItem.id}>
                            <td>
                              <button className="text-primary border-0 bg-transparent" onClick={() => handleViewDetails(caseItem.id)}>
                                {caseItem.id}
                              </button>
                            </td>
                            <td>
                              <button className="text-primary border-0 bg-transparent" onClick={() => handleViewDetails(caseItem.id)}>
                                {caseItem.case_name}
                              </button>
                            </td>
                            <td>{caseItem.from_medical_center?.name || 'N/A'}</td>
                            <td>{caseItem.medical_center?.name || 'N/A'}</td>
                            <td>{caseItem.case_status === 2 ? 'Transfer' : caseItem.case_status === 1 ? 'New Case' : 'N/A'}</td>
                            <td>
                              {caseItem.created_at ? formatCaseDate(caseItem.created_at) : 'N/A'}
                            </td>
                            <td>
                              <button className="btn p-0 me-2 border-0" onClick={() => handleResubmit(caseItem.id)} disabled={isLoading}>
                                <i className="ti ti-refresh text-info fs-5"></i>
                              </button>
                              <button className="btn p-0 me-2 border-0" onClick={() => handleDelete(caseItem.id)} disabled={isLoading}>
                                <i className="ti ti-trash text-danger fs-5"></i>
                              </button>
                              <button className="btn p-0 border-0" onClick={() => handleViewDetails(caseItem.id)} disabled={isLoading}>
                                <i className="ti ti-list-details text-success fs-5"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination for hold cases */}
                {filteredHoldCases.length > 0 && (
                  <Pagination total={filteredHoldCases.length} limit={limit} page={holdPage} onPageChange={setHoldPage} disabled={isLoading} />
                )}
                {/* Map section for paramedic locations */}
                <div className="card w-100 position-relative overflow-hidden">
                  <div className="px-4 py-3">
                    <h5 className="card-title fw-semibold mb-0 lh-sm">Paramedic Locations</h5>
                    <p className="card-subtitle mb-0 mt-1">View Paramedic locations on the map</p>
                  </div>
                  <div className="card-body p-4">
                    <div className="mb-4">
                      {/* Map iframe */}
                      <iframe
                        src="/map.html"
                        title="Map"
                        style={{ border: "none", width: "100%", height: "550px", overflow: "hidden" }}
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Case Modals */}
      {isEditMode ? (
        <EditCaseModal
          show={showModal}
          onClose={() => setShowModal(false)}
          formData={formData}
          onChange={handleInputChange}
          onSave={handleSave}
          isLoading={isLoading}
        />
      ) : (
        <AddCaseModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          isLoading={isLoading}
        />
      )}
      {/* Transfer Case Modal */}
      <TransferCaseModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        mode={transferModalMode}
        transferCaseData={selectedTransferCase}
        refreshList={fetchAllCases}
      />
    </div>
  );
};

export default CasesTab;